export type Vec3 = { x: number; y: number; z: number }

export const vecLen = (v: Vec3): number =>
  Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z)

export const vecNormalize = (v: Vec3): Vec3 => {
  const len = vecLen(v)
  if (len < 1e-8) return { x: 0, y: 0, z: 0 }
  return { x: v.x / len, y: v.y / len, z: v.z / len }
}

export const vecCross = (a: Vec3, b: Vec3): Vec3 => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
})

export const vecDot = (a: Vec3, b: Vec3): number =>
  a.x * b.x + a.y * b.y + a.z * b.z

/**
 * Signed distance from a point to a plane (origin + normal).
 * Returns the distance in centimeters.
 * Positive = point is on the side the normal points toward.
 */
export const signedDistanceToPlane = (
  point: Vec3,
  planeOrigin: Vec3,
  planeNormal: Vec3,
  measurementScale: number
): number => {
  const dx = point.x - planeOrigin.x
  const dy = point.y - planeOrigin.y
  const dz = point.z - planeOrigin.z
  return (
    (dx * planeNormal.x + dy * planeNormal.y + dz * planeNormal.z) *
    measurementScale
  )
}

export interface OrientationAxesLike {
  orientationVector: Vec3
  orientationNameFront: string
  orientationNameBack: string
  orientationNameLeft: string
  orientationNameRight: string
  orientationNameUp: string
  orientationNameDown: string
}

/**
 * Determines the orientation-axis direction label for a point's displacement
 * from a reference plane. Projects the plane normal onto the 3 basis vectors
 * derived from the orientation forward vector (Unreal left-handed, Z-up) to
 * find the dominant axis, then uses the sign of the signed distance to pick
 * the positive or negative name (e.g. "UP" vs "DOWN").
 */
export const getDirectionLabel = (
  planeNormal: Vec3,
  signedDistance: number,
  axes: OrientationAxesLike
): string => {
  const fwd = vecNormalize(axes.orientationVector)
  const worldUp: Vec3 = { x: 0, y: 0, z: 1 }

  let rawRight = vecCross(worldUp, fwd)
  if (vecLen(rawRight) < 1e-6) {
    rawRight = vecCross({ x: 0, y: 1, z: 0 }, fwd)
  }
  const right = vecNormalize(rawRight)
  const up = vecNormalize(vecCross(fwd, right))

  const dotFwd = vecDot(planeNormal, fwd)
  const dotRight = vecDot(planeNormal, right)
  const dotUp = vecDot(planeNormal, up)

  const absFwd = Math.abs(dotFwd)
  const absRight = Math.abs(dotRight)
  const absUp = Math.abs(dotUp)

  let dominantDot: number
  let positiveName: string
  let negativeName: string

  if (absFwd >= absRight && absFwd >= absUp) {
    dominantDot = dotFwd
    positiveName = axes.orientationNameFront
    negativeName = axes.orientationNameBack
  } else if (absRight >= absUp) {
    dominantDot = dotRight
    positiveName = axes.orientationNameRight
    negativeName = axes.orientationNameLeft
  } else {
    dominantDot = dotUp
    positiveName = axes.orientationNameUp
    negativeName = axes.orientationNameDown
  }

  return signedDistance * dominantDot >= 0 ? positiveName : negativeName
}

// ---------------------------------------------------------------------------
// Autolocate range computation
// ---------------------------------------------------------------------------

export interface PlaneDistanceRange {
  planeId: string
  planeLabel: string
  alias: string
  minSignedCm: number
  maxSignedCm: number
  straddles: boolean
}

interface Planelike {
  id: string
  label: string
  origin?: Vec3
  normal?: Vec3
  annotationAlias?: string
  showInAnnotation?: boolean
  isFrame?: boolean
  frameNumber?: number
}

/**
 * For each visible non-frame plane, computes the min/max signed distance
 * across all perimeter points. O(n * p) where n = points, p = planes.
 * Frame planes are excluded here — use computeClosestFrame for frames.
 */
export function computePlaneDistanceRanges(
  points: Vec3[],
  planes: Planelike[],
  measurementScale: number
): PlaneDistanceRange[] {
  const results: PlaneDistanceRange[] = []

  for (const plane of planes) {
    if (!plane.showInAnnotation || !plane.origin || !plane.normal) continue
    if (plane.isFrame) continue

    let minSigned = Infinity
    let maxSigned = -Infinity

    for (const pt of points) {
      const d = signedDistanceToPlane(
        pt,
        plane.origin,
        plane.normal,
        measurementScale
      )
      if (d < minSigned) minSigned = d
      if (d > maxSigned) maxSigned = d
    }

    if (!isFinite(minSigned)) continue

    results.push({
      planeId: plane.id,
      planeLabel: plane.label,
      alias: plane.annotationAlias || '',
      minSignedCm: minSigned,
      maxSignedCm: maxSigned,
      straddles: minSigned < 0 && maxSigned > 0,
    })
  }

  return results
}

/**
 * Finds the single closest frame to the centroid via O(1) projection, then
 * computes the min/max signed distance from all perimeter points to that
 * frame in O(n). Returns a PlaneDistanceRange for the closest frame, which
 * feeds into formatPlaneRange identically to non-frame planes.
 */
export function computeClosestFrame(
  centroid: Vec3,
  perimeterPoints: Vec3[],
  framePlanes: Planelike[],
  measurementScale: number
): PlaneDistanceRange | null {
  if (framePlanes.length < 2) return null

  const normal = framePlanes[0].normal
  const oStart = framePlanes[0].origin
  const oEnd = framePlanes[framePlanes.length - 1].origin
  if (!normal || !oStart || !oEnd) return null

  const fStart = vecDot(oStart, normal)
  const fEnd = vecDot(oEnd, normal)
  const totalSegments = framePlanes.length - 1
  const delta = (fEnd - fStart) / totalSegments

  if (Math.abs(delta) < 1e-12) return null

  const p = vecDot(centroid, normal)
  const rawIdx = (p - fStart) / delta
  const closestIdx = Math.max(0, Math.min(totalSegments, Math.round(rawIdx)))

  const frame = framePlanes[closestIdx]
  if (!frame.origin || !frame.normal) return null

  let minSigned = Infinity
  let maxSigned = -Infinity

  for (const pt of perimeterPoints) {
    const d = signedDistanceToPlane(
      pt,
      frame.origin,
      frame.normal,
      measurementScale
    )
    if (d < minSigned) minSigned = d
    if (d > maxSigned) maxSigned = d
  }

  if (!isFinite(minSigned)) return null

  return {
    planeId: frame.id,
    planeLabel: frame.label,
    alias: frame.annotationAlias || '',
    minSignedCm: minSigned,
    maxSignedCm: maxSigned,
    straddles: minSigned < 0 && maxSigned > 0,
  }
}

export interface FormattedPlaneRange {
  distance: string
  directionLabel?: string
  spanLabel?: string
  straddles: boolean
}

/**
 * Formats a PlaneDistanceRange into display strings.
 *
 * Non-straddle: distance = "4.00 – 7.00 ft", directionLabel = "UP"
 * Straddle:     distance = "0.30 ft AFT – 1.00 ft FWD", directionLabel = undefined
 *
 * The `straddles` flag tells the UI to always render "of {planeLabel}" even
 * though directionLabel is absent (the directions are inline in the distance).
 */
export function formatPlaneRange(
  range: PlaneDistanceRange,
  convert: (cm: number) => number,
  unitLabel: string,
  axes?: OrientationAxesLike | null,
  planeNormal?: Vec3
): FormattedPlaneRange {
  const span = convert(Math.abs(range.maxSignedCm - range.minSignedCm))
  const isPoint = Math.abs(range.maxSignedCm - range.minSignedCm) < 0.01

  if (range.straddles && axes && planeNormal) {
    const posLabel = getDirectionLabel(planeNormal, 1, axes)
    const negLabel = getDirectionLabel(planeNormal, -1, axes)
    const belowVal = convert(Math.abs(range.minSignedCm))
    const aboveVal = convert(Math.abs(range.maxSignedCm))
    return {
      distance: `${belowVal.toFixed(2)} ${unitLabel} ${negLabel} \u2013 ${aboveVal.toFixed(2)} ${unitLabel} ${posLabel}`,
      spanLabel: `${span.toFixed(2)} ${unitLabel} span`,
      straddles: true,
    }
  }

  const direction =
    axes && planeNormal
      ? getDirectionLabel(planeNormal, range.minSignedCm, axes)
      : undefined

  if (isPoint) {
    return {
      distance: `${convert(Math.abs(range.minSignedCm)).toFixed(2)} ${unitLabel}`,
      directionLabel: direction,
      straddles: false,
    }
  }

  const minAbs = convert(Math.abs(range.minSignedCm))
  const maxAbs = convert(Math.abs(range.maxSignedCm))
  const lo = Math.min(minAbs, maxAbs)
  const hi = Math.max(minAbs, maxAbs)

  return {
    distance: `${lo.toFixed(2)} \u2013 ${hi.toFixed(2)} ${unitLabel}`,
    directionLabel: direction,
    spanLabel: `${span.toFixed(2)} ${unitLabel} span`,
    straddles: false,
  }
}
